import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { getPendingUsers, approveUser, rejectUser, deleteUser } from '../services/api';
import type { User } from '../types/user';

export default function UserApproval() {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: pendingUsers = [], isLoading } = useQuery({
        queryKey: ['users', 'pending'],
        queryFn: getPendingUsers,
    });

    const approveMutation = useMutation({
        mutationFn: (userId: number) => approveUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users', 'pending'] });
            toast({ title: 'Usuário aprovado com sucesso' });
        },
        onError: () => {
            toast({ variant: 'destructive', title: 'Erro ao aprovar usuário' });
        },
    });

    const rejectMutation = useMutation({
        mutationFn: (userId: number) => rejectUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users', 'pending'] });
            toast({ title: 'Usuário rejeitado' });
        },
        onError: () => {
            toast({ variant: 'destructive', title: 'Erro ao rejeitar usuário' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (userId: number) => deleteUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users', 'pending'] });
            toast({ title: 'Usuário removido' });
        },
        onError: () => {
            toast({ variant: 'destructive', title: 'Erro ao remover usuário' });
        },
    });

    const isActing = () =>
        approveMutation.isPending ||
        rejectMutation.isPending ||
        deleteMutation.isPending;

    if (isLoading) {
        return (
            <div className="p-6">
                <p className="text-gray-500">Carregando...</p>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Aprovação de usuários</h1>
                <p className="mt-1 text-sm text-gray-600">
                    Usuários aguardando aprovação para acessar o sistema
                </p>
            </div>

            {pendingUsers.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">Nenhum usuário pendente de aprovação.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium text-gray-600">Nome</th>
                                <th className="px-4 py-3 text-left font-medium text-gray-600">Email</th>
                                <th className="px-4 py-3 text-left font-medium text-gray-600">Cadastro</th>
                                <th className="px-4 py-3 text-right font-medium text-gray-600">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {pendingUsers.map((user: User) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-900">{user.name}</td>
                                    <td className="px-4 py-3 text-gray-600">{user.email}</td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-2">
                                            <Button
                                                size="sm"
                                                onClick={() => approveMutation.mutate(user.id)}
                                                disabled={isActing()}
                                            >
                                                Aprovar
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => rejectMutation.mutate(user.id)}
                                                disabled={isActing()}
                                            >
                                                Rejeitar
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => deleteMutation.mutate(user.id)}
                                                disabled={isActing()}
                                            >
                                                Remover
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
